import { html } from 'lit/static-html.js'
import type { PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'

import { compose } from '#utils/mixins'
import { LfuiElement } from '#utils/element'

import styles from './lfds-list-row-content.css.ts'
const SLOT_NAMES = [
	'label-start',
	'sub-label-start',
	'label-end',
	'sub-label-end'
] as const
type SlotName = (typeof SLOT_NAMES)[number]
const isSlotName = (name: string): name is SlotName =>
	SLOT_NAMES.includes(name as SlotName)

/**
 * @name LfdsListRowContent
 *
 * @status ready
 * @tag lfds-list-row-content
 *
 * @slot label-start
 * @slot sub-label-start
 * @slot label-end
 * @slot sub-label-end
 */
export default class LfdsListRowContent extends compose(LfuiElement) {
	static styles = [LfuiElement.styles, styles]

	/**
	 * Controls the reading order of the slot content for screen readers.
	 *
	 * - `vertical` (default): reads column by column
	 *   → label-start, sub-label-start, label-end, sub-label-end
	 * - `horizontal`: reads row by row
	 *   → label-start, label-end, sub-label-start, sub-label-end
	 *
	 * Visual layout is unchanged — only DOM order (and thus screen reader
	 * announcement order) is affected.
	 */
	@property({ attribute: 'reading-order' })
	readingOrder: 'vertical' | 'horizontal' = 'vertical'
	#slotElements = new Map<SlotName, HTMLSlotElement>()
	#didInitSlotState = false

	connectedCallback() {
		super.connectedCallback()

		// Avoid mutating attributes in constructor. Some browser/runtime paths
		// (notably when instantiated via React wrappers) can throw
		// "NotSupportedError: Operation is not supported" for this.
		if (!this.#didInitSlotState) {
			for (const name of SLOT_NAMES) {
				this.#setSlotState(name, false)
			}
			this.#didInitSlotState = true
		}
	}

	protected firstUpdated(changedProperties: PropertyValues<this>) {
		super.firstUpdated(changedProperties)

		for (const name of SLOT_NAMES) {
			const slot = this.renderRoot.querySelector<HTMLSlotElement>(
				`slot[name='${name}']`
			)
			if (slot) {
				this.#slotElements.set(name, slot)
			}
		}

		this.#syncAllSlots()
	}

	render() {
		return this.readingOrder === 'horizontal'
			? html`
					<div class="content">
						<div class="label-start">
							<slot
								name="label-start"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="label-end">
							<slot
								name="label-end"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="sub-label-start">
							<slot
								name="sub-label-start"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="sub-label-end">
							<slot
								name="sub-label-end"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
					</div>
				`
			: html`
					<div class="content">
						<div class="label-start">
							<slot
								name="label-start"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="sub-label-start">
							<slot
								name="sub-label-start"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="label-end">
							<slot
								name="label-end"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
						<div class="sub-label-end">
							<slot
								name="sub-label-end"
								@slotchange=${this.#handleSlotChange}
							></slot>
						</div>
					</div>
				`
	}

	#handleSlotChange(event: Event) {
		if (!(event.target instanceof HTMLSlotElement)) {
			return
		}

		if (!isSlotName(event.target.name)) {
			return
		}

		this.#syncSlot(event.target.name, event.target)
	}

	#syncAllSlots() {
		for (const name of SLOT_NAMES) {
			this.#syncSlot(name)
		}
	}

	#syncSlot(name: SlotName, slot?: HTMLSlotElement | null) {
		const cached = this.#slotElements.get(name)
		const target = slot ?? cached

		if (!target) {
			return
		}

		if (!cached && slot) {
			this.#slotElements.set(name, slot)
		}

		this.#setSlotState(name, this.#slotHasContent(target))
	}

	#setSlotState(name: SlotName, hasContent: boolean) {
		const attr = `data-has-${name}`
		const value = hasContent ? 'true' : 'false'

		if (this.getAttribute(attr) !== value) {
			this.setAttribute(attr, value)
		}
	}

	#slotHasContent(slot: HTMLSlotElement) {
		return slot.assignedNodes({ flatten: true }).some((node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				return (node.textContent ?? '').trim().length > 0
			}

			if (node.nodeType === Node.COMMENT_NODE) {
				return false
			}

			return true
		})
	}
}
